import { useState } from "react";
import Button from "shared/Button";

interface Props {
  onApprove: (note?: string) => Promise<void>;
  onReject: (note?: string) => Promise<void>;
}

/** Per-row review UI (note input + Approve/Reject/Cancel) shared by every
 * pending/approved/rejected request panel in this feature
 * (LeaveRequestsPanel, UpdateRequestsPanel). Deliberately owns its own
 * reviewing/note/busy state rather than sharing one field across every row
 * in the parent's list — a shared field needs extra machinery (a ref, to
 * dodge stale closures) to stop approving/rejecting one row from
 * clobbering a different row's in-progress review; per-row state makes
 * that class of bug structurally impossible instead of guarding against
 * it (previously duplicated per-file, each with its own version of that
 * same shared-state bug — one already fixed once, one not). */
export default function ReviewControls({ onApprove, onReject }: Props) {
  const [reviewing, setReviewing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (fn: (note?: string) => Promise<void>) => {
    setBusy(true);
    try {
      // On success this row's own status flips away from "pending" once
      // the parent reloads its list, so this component simply stops
      // rendering — no need to reset local state here. On failure (caught
      // and alerted by the parent's handler), staying open with whatever
      // was typed lets the admin retry without retyping the note.
      await fn(note.trim() || undefined);
    } finally {
      setBusy(false);
    }
  };

  if (!reviewing) {
    return (
      <Button size="sm" variant="outline" onClick={() => setReviewing(true)}>
        Review
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Review note (optional)"
        className="px-3 py-1.5 bg-surface-muted border border-border rounded-lg text-xs text-foreground font-inter focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="flex gap-2">
        <Button size="sm" variant="primary" disabled={busy} onClick={() => submit(onApprove)}>
          Approve
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => submit(onReject)}>
          Reject
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setReviewing(false); setNote(""); }}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
